import config from '@conf'
import { fileService } from '@api/file.service'
import { FileHelper } from './file.helper'
import { DOCUMENT_POLICIES } from '@const'

/**
 * Wrapper to simplify work with documents
 */
export class DocumentContainer {
  /**
   * @param {object} [opts]
   * @param {File} [opts.file] - file representing a document
   * @param {string} [opts.key] - key to access file in storage
   * @param {string} opts.name - file name
   * @param {string} opts.mime_type - file MIME type
   * @param {string} opts.type - document type {@link documentTypes} of the document (!! nothing common with MIME-type)
   */
  constructor (opts) {
    if ((!opts.key || opts.key.length === 0) && (!opts.file || opts.file.length === 0)) {
      throw new Error('Document initialized without raw file and file key')
    }

    this.mimeType = opts.mime_type
    this.file = opts.file ? opts.file : ''
    this.name = opts.name
    this.type = opts.type
    this.key = opts.key

    this._arrayBuffer = []
    this._privateUrl = ''
    this._dataUrl = ''
  }

  getDetailsForSave () {
    return {
      mime_type: this.mimeType,
      name: this.name,
      key: this.key
    }
  }

  getDetailsForUpload () {
    if (!DOCUMENT_POLICIES[this.type]) {
      throw new Error('Unknown document type: ' + this.type)
    }
    return {
      type: DOCUMENT_POLICIES[this.type],
      mimeType: this.mimeType === undefined || this.mimeType === ''
        ? (this.file ? this.file.type : '')
        : this.mimeType,
      file: this.file
    }
  }

  setKey (key) {
    if (key && key.length) {
      this.key = key
      this.file = ''
    } else {
      this.key = ''
    }
  }

  get isUploaded () {
    return (this.key && this.key.length && (!this.file || this.file.length === 0))
  }

  /**
   * URL from where the public file can be downloaded. (!! works only with public files, private urls should be derived by {@link derivePrivateUrl}
   * @returns {string} publicUrl
   */
  get publicUrl () {
    if (!this.key || this.key.length === 0) return ''
    return `${config.FILE_STORAGE}/${this.key}`
  }

  /**
   * URL from where the private file can be downloaded. Call {@link derivePrivateUrl} before using this getter
   * @returns {string} privateURL
   */
  get privateUrl () {
    return this._privateUrl
  }

  /**
   * Data URL of the new file to allow browser open it before uploaded. Call {@link deriveDataUrl} before using this getter
   * @returns {string} dataUrl
   */
  get dataUrl () {
    return this._dataUrl
  }

  /**
   * File converted to AB format. You should upload files in this format. Call {@link deriveArrayBuffer} before using this getter
   * @returns {ArrayBuffer} arrayBuffer
   */
  get arrayBuffer () {
    return this._arrayBuffer
  }

  async derivePrivateUrl () {
    if (!this.key || this.key.length === 0) {
      throw new Error('To derive private url file must be already uploaded and contain the key')
    }
    const details = await fileService.loadDocumentDetailsById(this.key)
    this._privateUrl = details.data('url')
  }

  async deriveDataUrl () {
    if (!this.file || this.file.length === 0) {
      throw new Error('Data url can be derived only if raw file is present')
    }
    this._dataUrl = await FileHelper.readFileAsDataURL(this.file)
  }

  async deriveArrayBuffer () {
    if (!this.file || this.file.length === 0) {
      throw new Error('Array buffer can be derived only if file is present')
    }
    this._arrayBuffer = await FileHelper.readFileAsArrayBuffer(this.file)
  }
}

/**
 * Wraps object with doc details view {@link DocumentContainer.getDetailsForSave} to {@link DocumentContainer} instance
 * @param {object} documents - list of documents where key is document type {@link documentTypes}, value - details
 * generated by {@link DocumentContainer.getDetailsForSave}
 * @returns {object} wrappedDocuments
 */
export function wrapDocuments (documents) {
  if (!documents) return {}
  return Object.entries(documents)
    .reduce((documents, [type, doc]) => {
      const opts = doc.front || doc
      if (opts && (opts.key || opts.file || opts.front)) {
        opts['type'] = type
        documents[type] = new DocumentContainer(opts)
      } else {
        documents[type] = null
      }
      return documents
    }, {})
}

export function unwrapDocuments (documents) {
  if (!documents) return {}
  return Object.entries(documents)
    .reduce((documents, [type, doc]) => {
      documents[type] = doc ? doc.getDetailsForSave() : null
      return documents
    }, {})
}



// WEBPACK FOOTER //
// ./src/js/helpers/DocumentContainer.js